import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { QueryClient } from 'react-query';

import type { Customer } from '../../pages/cardex/api/useCustomer';
import { estRequeteConversations } from '../api/useConversations';
import { useTirerPourActualiser } from './useTirerPourActualiser';

/** En deçà, revenir au premier plan ne recharge pas la liste (audit 2026-09-23, A2). */
const PREMIER_PLAN_SANS_RECHARGE_MS = 60_000;

interface UseActualisationListeParams {
    queryClient: QueryClient;
    refCorps: RefObject<HTMLDivElement>;
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    setPagesVues: Dispatch<SetStateAction<Customer[]>>;
    isLoading: boolean;
    isFetching: boolean;
    dataUpdatedAt: number;
}

/**
 * L'actualisation de la liste des conversations — extrait de #82 / audit 2026-09-23
 * (B1, B4, A2, M1, M7). Trois chemins mènent au même geste : le lien « Actualiser »,
 * le tirage vers le bas et le retour au premier plan au-delà de
 * `PREMIER_PLAN_SANS_RECHARGE_MS`.
 *
 * Actualiser = repartir en page 1. Recharger la seule page N laisserait les pages
 * déjà vues figées, alors que le tri par dernier message a bougé : doublons ou
 * lignes perdues.
 */
export function useActualisationListe({
    queryClient,
    refCorps,
    page,
    setPage,
    setPagesVues,
    isLoading,
    isFetching,
    dataUpdatedAt,
}: UseActualisationListeParams) {
    const actualiser = useCallback(() => {
        setPage(1);
        setPagesVues([]);
        void queryClient.invalidateQueries({
            predicate: estRequeteConversations,
            // Depuis la page N, la page 1 se charge au remontage de sa clé : recharger
            // la page N active serait une requête pour rien (relecture M1).
            refetchActive: page === 1,
        });
    }, [queryClient, page, setPage, setPagesVues]);

    /*
     * L'indicateur « Actualisation… » (48 px, dans le flux) ne répond qu'à une
     * demande de l'utilisateur, geste ou lien (relecture I2). Un rechargement de
     * fond — liste périmée remontée au retour d’un fil, premier plan — ne
     * l'affiche pas : sa disparition décalerait la position restaurée (B2).
     */
    const actualisationDemandee = useRef(false);
    const actualiserALaDemande = useCallback(() => {
        actualisationDemandee.current = true;
        actualiser();
    }, [actualiser]);

    useEffect(() => {
        if (!isFetching) actualisationDemandee.current = false;
    }, [isFetching]);

    /*
     * Le retour au premier plan est BORNÉ (audit 2026-09-23, A2) : un coup d'œil à
     * une autre app ne relance pas une requête de 0,6 à 40 s. Au-delà de 60 s, la
     * liste repart en page 1. Le lien et le geste, eux, restent immédiats.
     */
    const recuLe = useRef(dataUpdatedAt);
    recuLe.current = dataUpdatedAt;

    useEffect(() => {
        const surVisibilite = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - recuLe.current > PREMIER_PLAN_SANS_RECHARGE_MS) actualiser();
        };
        document.addEventListener('visibilitychange', surVisibilite);
        return () => document.removeEventListener('visibilitychange', surVisibilite);
    }, [actualiser]);

    // Glisser vers le bas pour actualiser — audit 2026-09-23 (B4), en plus du lien
    // « Actualiser ». Inactif pendant le chargement initial : rien à tirer encore.
    const { distance, pret } = useTirerPourActualiser(refCorps, actualiserALaDemande, { actif: !isLoading });

    return { actualiser, actualiserALaDemande, actualisationDemandee, distance, pret };
}
